// parsers.ts - Centralized file parsing for KML, GPX, CSV, SHP, GeoJSON

import Papa from 'papaparse';
import shp from 'shpjs';
import * as toGeoJSON from '@tmcw/togeojson';

export type SupportedFormat = 'csv' | 'geojson' | 'kml' | 'gpx' | 'shp';

export interface ParseResult {
  geojson: GeoJSON.FeatureCollection;
  featureCount: number;
  error?: string;
  needsManualMapping?: boolean;
  headers?: string[];
  sampleData?: any[];
}

export async function parseFile(file: File): Promise<ParseResult> {
  const ext = file.name.split('.').pop()?.toLowerCase();
  console.log('parseFile - file name:', file.name, 'detected extension:', ext);
  
  if (!ext) return { geojson: null as any, featureCount: 0, error: 'Unknown file extension' };

  try {
    if (ext === 'csv') {
      console.log('Parsing as CSV');
      return await parseCSV(file);
    } else if (ext === 'geojson' || ext === 'json') {
      console.log('Parsing as GeoJSON');
      return await parseGeoJSON(file);
    } else if (ext === 'kml') {
      console.log('Parsing as KML');
      return await parseKML(file);
    } else if (ext === 'gpx') {
      console.log('Parsing as GPX');
      return await parseGPX(file);
    } else if (ext === 'zip' || ext === 'shp') {
      console.log('Parsing as SHP');
      return await parseSHP(file);
    }
    console.log('Unsupported extension:', ext);
    return { geojson: null as any, featureCount: 0, error: 'Unsupported file format. Supported types: KML, GPX, CSV, SHP (as .shp or .zip), and GeoJSON.' };
  } catch (e) {
    console.log('Parse error:', e);
    return { geojson: null as any, featureCount: 0, error: (e as Error).message };
  }
}

// Enhanced CSV parsing with coordinate detection and system conversion
export async function parseCSV(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length) {
          resolve({ geojson: null as any, featureCount: 0, error: `CSV Error: ${results.errors[0].message}` });
          return;
        }

        const headers = results.meta.fields;
        if (!headers) {
          resolve({ geojson: null as any, featureCount: 0, error: 'Could not detect headers in the CSV file.' });
          return;
        }
        
        const { latField, lngField } = findCoordinateHeaders(headers);

        if (!latField || !lngField) {
          resolve({
            geojson: { type: 'FeatureCollection', features: [] },
            featureCount: 0,
            needsManualMapping: true,
            headers,
            sampleData: (results.data as any[]).slice(0, 5),
            error: `Could not auto-detect latitude and longitude columns. Please map them manually.`,
          });
          return;
        }

        // Analyze a sample of coordinate values to determine format
        const sampleData = (results.data as any[]).slice(0, 5);
        const coordinateFormat = detectCoordinateFormat(sampleData, latField, lngField);

        const features = (results.data as any[])
          .map((row): GeoJSON.Feature | null => {
            const latVal = row[latField];
            const lngVal = row[lngField];
            
            if (latVal === undefined || lngVal === undefined || 
                latVal === '' || lngVal === '') return null;

            try {
              let lat: number, lng: number;

              if (coordinateFormat === 'UTM') {
                const converted = convertUTMToDD(parseFloat(lngVal), parseFloat(latVal));
                if (!converted) return null;
                lng = converted.longitude;
                lat = converted.latitude;
              } else if (coordinateFormat === 'DMS') {
                lat = convertDMSToDD(latVal.toString());
                lng = convertDMSToDD(lngVal.toString());
              } else {
                // Decimal degrees
                lat = parseFloat(latVal);
                lng = parseFloat(lngVal);
              }

              if (isNaN(lat) || isNaN(lng)) return null;

              // Validate coordinate ranges
              if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

              return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lng, lat] },
                properties: { ...row },
              };
            } catch (error) {
              console.warn('Error processing coordinate:', error);
              return null;
            }
          })
          .filter((f): f is GeoJSON.Feature => !!f);
          
        if (features.length === 0) {
          resolve({
            geojson: null as any,
            featureCount: 0,
            error: `No valid coordinate pairs found using headers '${latField}' and '${lngField}'. Check your data for empty or invalid values.`
          });
          return;
        }

        resolve({
          geojson: { type: 'FeatureCollection', features },
          featureCount: features.length,
        });
      },
      error: (err) => {
        resolve({ geojson: null as any, featureCount: 0, error: `CSV Read Error: ${err.message}` });
      },
    });
  });
}

function findCoordinateHeaders(headers: string[]): { latField: string | null, lngField: string | null } {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
  
  const latSynonyms = ['lat', 'latitude', 'y', 'northing'];
  const lngSynonyms = ['lng', 'lon', 'long', 'longitude', 'x', 'easting'];

  let latField: string | null = null;
  let lngField: string | null = null;

  for (const synonym of latSynonyms) {
    const foundIndex = normalizedHeaders.findIndex(h => h.includes(synonym));
    if (foundIndex !== -1) {
      latField = headers[foundIndex];
      break;
    }
  }

  for (const synonym of lngSynonyms) {
    const foundIndex = normalizedHeaders.findIndex(h => h.includes(synonym));
    if (foundIndex !== -1) {
      lngField = headers[foundIndex];
      break;
    }
  }

  return { latField, lngField };
}

function detectCoordinateFormat(sampleData: any[], latField: string, lngField: string): 'DD' | 'UTM' | 'DMS' {
  // Check a few sample values to determine the coordinate format
  for (const row of sampleData) {
    const latVal = row[latField];
    const lngVal = row[lngField];
    
    if (!latVal || !lngVal) continue;
    
    const latStr = latVal.toString();
    const lngStr = lngVal.toString();
    
    // Check for DMS format (contains °, ', or ")
    if (latStr.includes('°') || latStr.includes("'") || latStr.includes('"') ||
        lngStr.includes('°') || lngStr.includes("'") || lngStr.includes('"')) {
      return 'DMS';
    }
    
    const latNum = parseFloat(latVal);
    const lngNum = parseFloat(lngVal);
    
    if (isNaN(latNum) || isNaN(lngNum)) continue;
    
    // Check for UTM format (large coordinate values)
    // UTM Easting is typically 100,000 - 999,999
    // UTM Northing is typically 0 - 10,000,000+
    if ((lngNum >= 100000 && lngNum <= 999999) && 
        (latNum >= 0 && latNum <= 10000000)) {
      return 'UTM';
    }
    
    // Check for decimal degrees (reasonable lat/lng ranges)
    if (latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180) {
      return 'DD';
    }
  }
  
  // Default to decimal degrees
  return 'DD';
}

function convertUTMToDD(easting: number, northing: number, zone: number = 29): { latitude: number; longitude: number } | null {
  // Simplified UTM to Geographic conversion for Mauritania (Zone 29N)
  // This is a basic implementation - for production use, consider using a proper projection library
  
  try {
    // UTM Zone 29N parameters for Mauritania
    const centralMeridian = -9; // Zone 29 central meridian
    const scaleFactor = 0.9996;
    const falseEasting = 500000;
    const falseNorthing = 0; // Northern hemisphere
    
    // Remove false easting/northing
    const x = easting - falseEasting;
    const y = northing - falseNorthing;
    
    // Basic conversion (simplified)
    // Note: This is an approximation. For precise conversion, use proj4 or similar
    const longitude = centralMeridian + (x / (scaleFactor * 111320)) * (180 / Math.PI);
    const latitude = (y / (scaleFactor * 110540)) * (180 / Math.PI);
    
    // Validate results are within reasonable bounds for Mauritania
    if (latitude >= 14 && latitude <= 27 && longitude >= -17 && longitude <= -5) {
      return { latitude, longitude };
    }
    
    return null;
  } catch (error) {
    console.warn('UTM conversion error:', error);
    return null;
  }
}

function convertDMSToDD(dmsString: string): number {
  // Parse DMS format: "25°30'15.6"N" or "25 30 15.6" or "25°30.26'"
  const cleanStr = dmsString.toString().trim().toUpperCase();
  
  // Remove cardinal directions and clean the string
  const coordStr = cleanStr.replace(/[NSEW]/g, '').trim();
  
  // Extract degrees, minutes, seconds
  let degrees = 0, minutes = 0, seconds = 0;
  
  // Try different DMS patterns
  let match = coordStr.match(/(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*/);
  if (match) {
    degrees = parseFloat(match[1]);
    minutes = parseFloat(match[2]);
    seconds = parseFloat(match[3]);
  } else {
    // Try degrees and decimal minutes: "25°30.26'"
    match = coordStr.match(/(\d+)[°\s]+(\d+\.?\d*)['\s]*/);
    if (match) {
      degrees = parseFloat(match[1]);
      minutes = parseFloat(match[2]);
      seconds = 0;
    } else {
      // Try just degrees: "25.5°"
      match = coordStr.match(/(\d+\.?\d*)[°\s]*/);
      if (match) {
        degrees = parseFloat(match[1]);
        minutes = 0;
        seconds = 0;
      } else {
        throw new Error('Could not parse DMS format');
      }
    }
  }
  
  // Convert to decimal degrees
  let dd = degrees + (minutes / 60) + (seconds / 3600);
  
  // Apply negative sign for South/West
  if (cleanStr.includes('S') || cleanStr.includes('W')) {
    dd = -dd;
  }
  
  return dd;
}

// Parse CSV with manually specified latitude and longitude columns
export async function parseCSVWithMapping(
  file: File, 
  latColumn: string, 
  lngColumn: string
): Promise<ParseResult> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length) {
          resolve({ geojson: null as any, featureCount: 0, error: `CSV Error: ${results.errors[0].message}` });
          return;
        }

        // Analyze coordinate format using the specified columns
        const sampleData = (results.data as any[]).slice(0, 5);
        const coordinateFormat = detectCoordinateFormat(sampleData, latColumn, lngColumn);

        const features = (results.data as any[])
          .map((row): GeoJSON.Feature | null => {
            const latVal = row[latColumn];
            const lngVal = row[lngColumn];
            
            if (latVal === undefined || lngVal === undefined || 
                latVal === '' || lngVal === '') return null;

            try {
              let lat: number, lng: number;

              if (coordinateFormat === 'UTM') {
                const converted = convertUTMToDD(parseFloat(lngVal), parseFloat(latVal));
                if (!converted) return null;
                lng = converted.longitude;
                lat = converted.latitude;
              } else if (coordinateFormat === 'DMS') {
                lat = convertDMSToDD(latVal.toString());
                lng = convertDMSToDD(lngVal.toString());
              } else {
                // Decimal degrees
                lat = parseFloat(latVal);
                lng = parseFloat(lngVal);
              }

              if (isNaN(lat) || isNaN(lng)) return null;

              // Validate coordinate ranges
              if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

              return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lng, lat] },
                properties: { ...row },
              };
            } catch (error) {
              console.warn('Error processing coordinate:', error);
              return null;
            }
          })
          .filter((f): f is GeoJSON.Feature => !!f);
          
        if (features.length === 0) {
          resolve({
            geojson: null as any,
            featureCount: 0,
            error: `No valid coordinate pairs found using columns '${latColumn}' and '${lngColumn}'. Check your data for empty or invalid values.`
          });
          return;
        }

        resolve({
          geojson: { type: 'FeatureCollection', features },
          featureCount: features.length,
        });
      },
      error: (err) => {
        resolve({ geojson: null as any, featureCount: 0, error: `CSV Read Error: ${err.message}` });
      },
    });
  });
}

export async function parseGeoJSON(file: File): Promise<ParseResult> {
  const text = await file.text();
  try {
    const geojson = JSON.parse(text);
    if (geojson.type !== 'FeatureCollection') {
      return { geojson: null as any, featureCount: 0, error: 'GeoJSON must be a FeatureCollection.' };
    }
    return { geojson, featureCount: geojson.features.length };
  } catch (e) {
    return { geojson: null as any, featureCount: 0, error: 'Invalid GeoJSON.' };
  }
}

export async function parseKML(file: File): Promise<ParseResult> {
  const text = await file.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  try {
    const rawGeojson = toGeoJSON.kml(xml);
    const filteredFeatures = rawGeojson.features.filter(
      (feature): feature is GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> =>
        feature.geometry !== null
    );
    return {
      geojson: {
        ...rawGeojson,
        features: filteredFeatures,
      },
      featureCount: filteredFeatures.length,
    };
  } catch (e) {
    return { geojson: null as any, featureCount: 0, error: 'Failed to parse KML.' };
  }
}

export async function parseGPX(file: File): Promise<ParseResult> {
  const text = await file.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  try {
    const rawGeojson = toGeoJSON.gpx(xml);
    const filteredFeatures = rawGeojson.features.filter(
      (feature): feature is GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> =>
        feature.geometry !== null
    );
    return {
      geojson: {
        ...rawGeojson,
        features: filteredFeatures,
      },
      featureCount: filteredFeatures.length,
    };
  } catch (e) {
    return { geojson: null as any, featureCount: 0, error: 'Failed to parse GPX.' };
  }
}

export async function parseSHP(file: File): Promise<ParseResult> {
  try {
    console.log('Parsing shapefile:', file.name, 'size:', file.size, 'type:', file.type);
    
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer size:', arrayBuffer.byteLength);
    
    // Check if the file is empty or too small
    if (arrayBuffer.byteLength === 0) {
      return { geojson: null as any, featureCount: 0, error: 'File is empty.' };
    }
    
    if (arrayBuffer.byteLength < 100) {
      return { geojson: null as any, featureCount: 0, error: 'File is too small to be a valid shapefile.' };
    }
    
    // Try to parse with shpjs
    const geojson = await shp(arrayBuffer);
    
    console.log('Raw parsed result:', geojson);
    console.log('Type of result:', typeof geojson);
    console.log('Result keys:', geojson ? Object.keys(geojson) : 'null');
    
    // Validate the result
    if (!geojson) {
      return { geojson: null as any, featureCount: 0, error: 'No data found in shapefile.' };
    }
    
    // Check if it's an array of layers (some shapefiles return multiple layers)
    if (Array.isArray(geojson)) {
      console.log('Shapefile contains multiple layers:', geojson.length);
      
      // Find the first layer with features
      for (let i = 0; i < geojson.length; i++) {
        const layer = geojson[i];
        console.log(`Layer ${i}:`, layer);
        
        if (layer && layer.features && Array.isArray(layer.features) && layer.features.length > 0) {
          console.log(`Using layer ${i} with ${layer.features.length} features`);
          return { geojson: layer, featureCount: layer.features.length };
        }
      }
      
      return { geojson: null as any, featureCount: 0, error: `Shapefile contains ${geojson.length} layers but none have features.` };
    }
    
    // Single layer shapefile
    if (!geojson.features || !Array.isArray(geojson.features)) {
      console.log('Invalid features structure:', geojson.features);
      return { geojson: null as any, featureCount: 0, error: 'Invalid shapefile structure - no features array found.' };
    }
    
    if (geojson.features.length === 0) {
      console.log('Features array is empty');
      return { geojson: null as any, featureCount: 0, error: 'Shapefile contains no geographic features.' };
    }
    
    console.log('Successfully parsed shapefile with', geojson.features.length, 'features');
    console.log('First feature sample:', geojson.features[0]);
    return { geojson, featureCount: geojson.features.length };
    
  } catch (e) {
    console.error('Shapefile parsing error:', e);
    
    const errorMessage = e instanceof Error ? e.message : 'Unknown error';
    
    // Provide more specific error messages based on common issues
    if (errorMessage.includes('zip')) {
      return { geojson: null as any, featureCount: 0, error: 'Invalid ZIP file format. Please ensure the shapefile is properly zipped with all required components (.shp, .shx, .dbf).' };
    }
    
    if (errorMessage.includes('shp')) {
      return { geojson: null as any, featureCount: 0, error: 'Invalid shapefile format. Please ensure all required files (.shp, .shx, .dbf) are included in the ZIP.' };
    }
    
    if (errorMessage.includes('dbf')) {
      return { geojson: null as any, featureCount: 0, error: 'Missing or invalid .dbf file. Shapefiles require a .dbf file for attribute data.' };
    }
    
    return { geojson: null as any, featureCount: 0, error: `Failed to parse shapefile: ${errorMessage}` };
  }
}
